import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import {
  HubspotContactSalesFormKind,
  resolveHubspotContactSalesFormId,
  resolveHubspotPortalId,
  resolveHubspotRegion
} from 'src/app/constants/hubspot.constants';
import { HubspotFormDialogComponent, HubspotFormDialogData } from './hubspot-form-dialog.component';

/**
 * Configuration options for the HubSpot form
 */
export interface HubspotFormConfig {
  portalId?: string;
  formId?: string;
  region?: string;
  target?: string;
  onFormReady?: (form: any) => void;
  onFormSubmit?: (form: any) => void;
  onFormSubmitted?: (form: any) => void;
  // Custom properties for checkbox selection
  selectCheckbox?: string; // ID or name of the checkbox to select
  addonType?: string; // Type of addon that was selected
  [key: string]: any; // Allow for additional HubSpot form configuration options
}

/**
 * Configuration options for the HubSpot dialog
 */
export interface HubspotDialogConfig {
  title?: string;
  width?: string;
  height?: string;
  maxWidth?: string;
  maxHeight?: string;
  disableClose?: boolean;
  panelClass?: string | string[];
  formConfig?: HubspotFormConfig;
}

export interface HubspotContactSalesDialogOptions {
  title?: string;
  formKind: HubspotContactSalesFormKind;
  featureInterest: string;
  selectCheckbox?: string;
  width?: string;
  maxWidth?: string;
  panelClass?: string | string[];
}

/**
 * Service for opening HubSpot form dialogs
 *
 * This service provides a convenient way to open a dialog containing a HubSpot form
 * with customizable configuration options.
 */
@Injectable({
  providedIn: 'root',
})
export class HubspotFormDialogService {
  constructor(
    private dialog: MatDialog,
  ) {
    // HubSpot script is loaded in main.ts
  }

  openHubspotFormDialog(config?: HubspotDialogConfig): Observable<unknown> {
    const dialogConfig = new MatDialogConfig<HubspotFormDialogData>();

    dialogConfig.width = config?.width || '600px';
    dialogConfig.maxWidth = config?.maxWidth || '95vw';
    dialogConfig.maxHeight = config?.maxHeight || '95vh';
    dialogConfig.disableClose = config?.disableClose || false;
    dialogConfig.panelClass = config?.panelClass || 'hubspot-form-dialog';
    dialogConfig.autoFocus = false; // Prevent automatic focus on the close button
    dialogConfig.restoreFocus = true; // Restore focus to the previously focused element when dialog closes

    // Merge defaults from environment so callers don't need to pass them
    const mergedFormConfig = {
      portalId: resolveHubspotPortalId('addon'),
      formId: resolveHubspotContactSalesFormId('addon'),
      region: resolveHubspotRegion(),
      ...(config?.formConfig || {}),
    } as HubspotFormConfig;

    dialogConfig.data = {
      title: config?.title,
      formConfig: mergedFormConfig,
    };

    const dialogRef: MatDialogRef<HubspotFormDialogComponent, unknown> =
      this.dialog.open(HubspotFormDialogComponent, dialogConfig);
    return dialogRef.afterClosed();
  }

  openContactSalesDialog(options: HubspotContactSalesDialogOptions): Observable<unknown> {
    const featureInterest = options.featureInterest;

    return this.openHubspotFormDialog({
      title: options.title ?? 'Contact Sales',
      width: options.width ?? '600px',
      maxWidth: options.maxWidth ?? '95vw',
      panelClass: options.panelClass ?? 'hubspot-form-dialog',
      formConfig: {
        portalId: resolveHubspotPortalId(options.formKind),
        formId: resolveHubspotContactSalesFormId(options.formKind),
        region: resolveHubspotRegion(),
        addonType: featureInterest,
        selectCheckbox: options.selectCheckbox ?? featureInterest,
      },
    });
  }
}
